import React from "react";
import { Trash2, AlertTriangle, ArrowRight } from "lucide-react";
import { CaseRow, CaseProductRow, CaseRoutingStatus } from "src/types";
import { ROUTING_TYPE_CONFIG } from "src/constants/relabelCases";

const CASE_CONFIG: Record<
  CaseRoutingStatus,
  {
    label: string;
    borderColor: string;
    bgColor: string;
    badgeBg: string;
    badgeText: string;
    instructionText: string;
    instruction: string;
  }
> = {
  EU_RELABEL: {
    ...ROUTING_TYPE_CONFIG.EU_RELABEL,
    instructionText: ROUTING_TYPE_CONFIG.EU_RELABEL.textColor,
    instruction: "Route to EU relabelling area (cartonised by Healf team)"
  },
  EU_STANDARD: {
    ...ROUTING_TYPE_CONFIG.EU_STANDARD,
    instructionText: ROUTING_TYPE_CONFIG.EU_STANDARD.textColor,
    instruction: "Route to EU relabelling area (cartonised by Healf team)"
  },
  MIXED: {
    label: "Mixed Types",
    borderColor: "border-red-500",
    bgColor: "bg-red-50",
    badgeBg: "bg-red-100",
    badgeText: "text-red-800",
    instructionText: "text-red-700",
    instruction: "Cannot generate: mixed stock types in a case"
  },
  MULTIPLE_PRODUCTS: {
    label: "Multiple Products",
    borderColor: "border-red-500",
    bgColor: "bg-red-50",
    badgeBg: "bg-red-100",
    badgeText: "text-red-800",
    instructionText: "text-red-700",
    instruction: "Cannot generate: each case may contain only one product"
  },
  EMPTY: {
    label: "Empty Case",
    borderColor: "border-secondary-300",
    bgColor: "bg-secondary-50",
    badgeBg: "bg-secondary-100",
    badgeText: "text-secondary-600",
    instructionText: "text-secondary-500",
    instruction: "Add products to this case"
  }
};

interface CaseCardProps {
  caseRow: CaseRow;
  onProductQtyChange: (caseNumber: number, productId: string, quantity: string) => void;
  onDeleteCase: (caseNumber: number) => void;
  isReadOnly: boolean;
  availableByProductId?: Record<string, number>;
  inProgressByProductId?: Record<string, number>;
}

const CaseCard: React.FC<CaseCardProps> = ({
  caseRow,
  onProductQtyChange,
  onDeleteCase,
  isReadOnly,
  availableByProductId = {},
  inProgressByProductId = {}
}) => {
  const config = CASE_CONFIG[caseRow.routingType];
  const totalUnits = caseRow.products.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);

  return (
    <div className={`${config.bgColor} ${config.borderColor} border-2 border-l-[6px] rounded-xl overflow-hidden`}>
      <div className="flex items-center justify-between px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-secondary-900">Case {caseRow.caseNumber}</span>
          <span className={`${config.badgeBg} ${config.badgeText} text-xs font-semibold px-2.5 py-1 rounded-full`}>
            {config.label}
          </span>
          <span className="text-xs text-secondary-500">{totalUnits} units</span>
        </div>
        {!isReadOnly && (
          <button
            type="button"
            onClick={() => onDeleteCase(caseRow.caseNumber)}
            className="p-1.5 rounded-md hover:bg-red-100 text-secondary-400 hover:text-red-600 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="px-5 pb-2">
        <div className="flex items-center gap-2">
          {caseRow.routingType === "MIXED" || caseRow.routingType === "MULTIPLE_PRODUCTS" ? (
            <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 ${config.instructionText}`} />
          ) : (
            <ArrowRight className={`w-3.5 h-3.5 flex-shrink-0 ${config.instructionText}`} />
          )}
          <p className={`${config.instructionText} font-medium text-xs`}>{config.instruction}</p>
        </div>
      </div>

      <div className="px-5 pb-4 flex flex-wrap gap-2">
        {caseRow.products.map((product: CaseProductRow) => {
          const rtCfg = ROUTING_TYPE_CONFIG[product.routingType as keyof typeof ROUTING_TYPE_CONFIG];
          const productAvailable = availableByProductId[product.id] ?? 0;
          const productInProgress = inProgressByProductId[product.id] ?? 0;
          const productInputQty = Number(product.quantity) || 0;
          const isOver = productInProgress > productAvailable && productInputQty > 0;
          const overBy = productInProgress - productAvailable;
          return (
            <div
              key={product.id}
              className={`inline-flex flex-col gap-1 border rounded-lg px-3 py-2 ${
                isOver ? "border-red-300 bg-red-50" : "border-secondary-200 bg-white/70"
              }`}
            >
              <div className="inline-flex items-center gap-2">
                {rtCfg ? (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${rtCfg.badgeBg} ${rtCfg.badgeText}`}>
                    {rtCfg.label}
                  </span>
                ) : null}
                <span className="font-mono text-sm font-bold text-secondary-800">{product.skuName}</span>
                <span className="text-sm text-secondary-500">{product.name}</span>
                <input
                  type="number"
                  readOnly={isReadOnly}
                  min="0"
                  value={product.quantity}
                  onChange={(e) => onProductQtyChange(caseRow.caseNumber, product.id, e.target.value)}
                  className={`w-20 text-center border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent ${
                    isOver ? "border-red-400 focus:ring-red-500" : "border-secondary-300 focus:ring-black"
                  }`}
                />
                <span className="text-xs text-secondary-400">/ {productAvailable} avail</span>
              </div>
              {isOver && (
                <p className="text-xs text-red-600 font-semibold">
                  {overBy} over — only {productAvailable} available across all in-progress cases
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { CaseCard };
